import history from '@history';
import { PipelinesPaths } from 'app/configs/routesConfig/pipelines';
import {
  ausFindingsApi,
  contactsApi,
  loanApplicationsProgressApi,
  loanConditionApiV2,
  loanPipelinesApi,
  taskApi,
} from 'app/services/api';
import { loanPipelinesEventEmitter } from 'app/services/eventEmitter';
import _ from 'lodash';
import { normalize, schema } from 'normalizr';

import {
  LoanPipelineRepresentationViewType,
  LoanPipelinesEmitterEventType,
} from 'constants/loanPipelines';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';
import { normalizeLoanPipelineViewColumnData } from 'utils/pipelines';

import {
  getLoanConditionsByIdsAction,
  updateLoanConditionAction,
} from 'store/loanApplications/actions';
import { selectLoanPipelineById } from 'store/loanPipelines/selectors';

const loanPipelineEntitiesSchema = new schema.Entity(
  'data',
  {},
  {
    idAttribute: 'entryId',
    processStrategy: data => ({
      ...data,
      columnValues: normalizeLoanPipelineViewColumnData(data.columnValues),
    }),
  },
);

export const LOAN_PIPELINE_ENTITIES_ACTION_TYPE = createNamedActions('loanPipelineEntities', {
  GET_PIPELINE_ENTITIES_DATA: 'GET_PIPELINE_ENTITIES_DATA',
  UPDATE_PIPELINE_ENTITY_DATA: 'UPDATE_PIPELINE_ENTITY_DATA',
  UPDATE_PIPELINE_ENTITY_DATA_V2: 'UPDATE_PIPELINE_ENTITY_DATA_V2',
  UPDATE_PARTIAL_LOADED_ENTITY_IDS: 'UPDATE_PARTIAL_LOADED_ENTITY_IDS',
  OVERRIDE_PIPELINE_ENTITY_DATA: 'OVERRIDE_PIPELINE_ENTITY_DATA',
  DELETE_PIPELINE_ENTITIES_DATA: 'DELETE_PIPELINE_ENTITY_DATA',
  RESET_PIPELINE_ENTITIES_DATA: 'RESET_PIPELINE_ENTITY_DATA',
  SET_BASIC_SUMMARY_DATA: 'SET__BASIC_SUMMARY_DATA',
  RESET_SUMMARY_DATA: 'RESET_SUMMARY_DATA',
  GET_AUS_FINDINGS_DATA: 'GET_AUS_FINDINGS_DATA',
  GET_LOAN_SUMMARY_TASK_DATA: 'GET_LOAN_SUMMARY_TASK_DATA',
  GET_LOAN_SUMMARY_CONDITIONS_DATA: 'GET_LOAN_SUMMARY_CONDITIONS_DATA',
  GET_LOAN_SUMMARY_CONTACTS_DATA: 'GET_LOAN_SUMMARY_CONTACTS_DATA',
  GET_LOAN_SUMMARY_PROGRESS_DATA: 'GET_LOAN_SUMMARY_PROGRESS_DATA',
});

export const setCurrentSummaryData =
  ({ applicationId, loanId } = {}) =>
  dispatch => {
    return dispatch({
      type: LOAN_PIPELINE_ENTITIES_ACTION_TYPE.SET_BASIC_SUMMARY_DATA,
      payload: { applicationId, loanId },
    });
  };

export const resetSummaryData = () => dispatch => {
  return dispatch({
    type: LOAN_PIPELINE_ENTITIES_ACTION_TYPE.RESET_SUMMARY_DATA,
  });
};

export const getLoanPipelineEntitiesDataAction = catchAsyncActionError(params => async dispatch => {
  const { _sortOrder, isFirstFetch, ...filteredParams } = params;
  const { data: responseData } = await loanPipelinesApi.getLoanPipelinesDynamicView(filteredParams);
  const { data, totalPages } = responseData;

  return dispatch({
    type: LOAN_PIPELINE_ENTITIES_ACTION_TYPE.GET_PIPELINE_ENTITIES_DATA,
    payload: normalize(data, [loanPipelineEntitiesSchema]).entities.data,
    meta: {
      ...params,
      signal: undefined,
      entryIds: _.map(data, 'entryId'),
      isLastFetch: totalPages === params.pageNumber,
    },
  });
});

export const getAusFindingsAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await ausFindingsApi.getAusFindings(params);

  return dispatch({
    type: LOAN_PIPELINE_ENTITIES_ACTION_TYPE.GET_AUS_FINDINGS_DATA,
    payload: data,
  });
});

export const getLoanSummaryTaskAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await taskApi.getLoanSummaryTask(params);

  return dispatch({
    type: LOAN_PIPELINE_ENTITIES_ACTION_TYPE.GET_LOAN_SUMMARY_TASK_DATA,
    payload: data,
  });
});

export const getLoanSummaryContactsAction = catchAsyncActionError(
  ({ applicationId }) =>
    async dispatch => {
      dispatch({
        type: LOAN_PIPELINE_ENTITIES_ACTION_TYPE.GET_LOAN_SUMMARY_CONTACTS_DATA,
        payload: [],
      });

      const { data } = await contactsApi.getLoanSummaryContacts({ applicationId });

      return dispatch({
        type: LOAN_PIPELINE_ENTITIES_ACTION_TYPE.GET_LOAN_SUMMARY_CONTACTS_DATA,
        payload: data,
      });
    },
);

export const getLoanSummaryConditionsAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await loanConditionApiV2.getLoanConditionSummary(params);

  return dispatch({
    type: LOAN_PIPELINE_ENTITIES_ACTION_TYPE.GET_LOAN_SUMMARY_CONDITIONS_DATA,
    payload: data,
  });
});

export const getLoanSummaryProgressAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await loanApplicationsProgressApi.getApplicationProgress(params);

  return dispatch({
    type: LOAN_PIPELINE_ENTITIES_ACTION_TYPE.GET_LOAN_SUMMARY_PROGRESS_DATA,
    payload: data,
  });
});

const deleteEntityDataInPipelineAction = data => ({
  type: LOAN_PIPELINE_ENTITIES_ACTION_TYPE.DELETE_PIPELINE_ENTITIES_DATA,
  payload: data,
});

const updatePipelinePartialLoadedEntityIdsAction = ({ entryIds, isDeleted }) => ({
  type: LOAN_PIPELINE_ENTITIES_ACTION_TYPE.UPDATE_PARTIAL_LOADED_ENTITY_IDS,
  payload: entryIds,
  meta: { isDeleted },
});

export const resetPipelineEntitiesAction = () => dispatch => {
  dispatch({ type: LOAN_PIPELINE_ENTITIES_ACTION_TYPE.RESET_PIPELINE_ENTITIES_DATA });
};

export const manageEntitiesChangedInPipelineAction = loanPipelineUpdateStatus => dispatch => {
  const { params } = PipelinesPaths.loanPipelinesById.matchPath(history.location.pathname) || {};

  const { addedEntityIds, updatedEntityIds, removedEntityIds, loanPipelineId } =
    loanPipelineUpdateStatus;

  const isLoanPipelineEntityChanged = params?.id === loanPipelineId;

  if (!isLoanPipelineEntityChanged) {
    return;
  }

  const uniqueRequestEntityIds = _.union(addedEntityIds, updatedEntityIds);

  if (removedEntityIds?.length) {
    dispatch(deleteEntityDataInPipelineAction(removedEntityIds));
  }

  if (uniqueRequestEntityIds.length) {
    return loanPipelinesEventEmitter.emit(LoanPipelinesEmitterEventType.RE_FETCH_ENTITIES, {
      entryId: uniqueRequestEntityIds,
      entryIds: uniqueRequestEntityIds,
    });
  }
};

export const signOffConditionInPipelineV2Action = catchAsyncActionError(
  params => async dispatch => {
    const { data: response } = await loanConditionApiV2.signOffViaPipeline(params);
    const { condition, pipelineEntryData } = response;

    dispatch(updateLoanConditionAction(condition));
    dispatch(updatePipelineEntityV2Action(pipelineEntryData.data));
  },
);

export const unsignOffConditionInPipelineV2Action = catchAsyncActionError(
  params => async dispatch => {
    const { data: response } = await loanConditionApiV2.unsignOffViaPipeline(params);
    const { condition, pipelineEntryData } = response;

    dispatch(updateLoanConditionAction(condition));
    dispatch(updatePipelineEntityV2Action(pipelineEntryData.data));
  },
);

export const updatePipelineEntityV2Action = data => dispatch => {
  const normalizedData = normalize(data, [loanPipelineEntitiesSchema]);

  return dispatch({
    type: LOAN_PIPELINE_ENTITIES_ACTION_TYPE.UPDATE_PIPELINE_ENTITY_DATA_V2,
    payload: normalizedData.entities.data[normalizedData.result],
  });
};

export const getLoanPipelineEntitiesSupportAction =
  ({ entryIds, signal, loanPipelineId }) =>
  async (dispatch, getState) => {
    const state = getState();
    const pipeline = selectLoanPipelineById(loanPipelineId)(state);

    if (!pipeline?.hasLazyViewColumns || !entryIds.length) {
      return;
    }

    try {
      dispatch(updatePipelinePartialLoadedEntityIdsAction({ entryIds, isDeleted: false }));

      if (pipeline.representationView.type === LoanPipelineRepresentationViewType.CONDITION) {
        await dispatch(getLoanConditionsByIdsAction({ loanConditionId: entryIds, signal }));
      }
    } catch {
      /* empty */
    } finally {
      dispatch(updatePipelinePartialLoadedEntityIdsAction({ entryIds, isDeleted: true }));
    }
  };
